import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/client";
import { Metric, Context } from "@amzn/katal-metrics";
import { Button, Dropdown, ButtonGroup } from "@amzn/alchemy-components-react";
import {PageType, TenantID, WorkflowState, WorkflowStatus, WorkflowType} from "src/common/enums";
import { NETWORK_ONLY, FIFTEEN_DAYS_IN_MILLISECONDS, TIME_MODE_OPTIONS,
    RELATIVE_TIME_RANGE_OPTIONS, TimeMode, RelativeTimeRange, ALL_SCOPES } from "src/common/constants";
import { convertDateTimeToLocaleFormat, convertDateToLocaleFormat } from "src/common/util";
import { LIST_WORKFLOWS_QUERY } from "src/common/gql-operations";
import { LocalizedDatepicker } from "src/components/localized-datepicker";
import { PageProps } from "src/pages/page-interface";
import { RenderWorkflowCards } from "src/pages/workflows/render-workflow-cards";
import { BreadCrumbs } from "src/components/breadcrumb";
import { CardItem } from "src/components/cards/card-item";
import { WorkflowDetails } from "src/models/workflow-details";
import { logger } from "src/logger";
import initialMetricsPublisher from 'src/metrics';
import { WorkflowCard } from "src/models/workflow-card";
import { useAuth } from 'src/components/auth/auth-provider';

const cloudWatchDimensions = [
    new Metric.String('page', 'workflow-list'),
]

// @ts-ignore
const additionalMetricsContext = new Context({cloudWatchDimensions});
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);

export const WorkflowList = (props: PageProps) => {

    const auth = useAuth();
    const {t} = useTranslation();
    const locale = document.documentElement.lang;   // e.g. en-US

    // This list only includes workflows that are implemented and relevant to users
    // - ALL isn't an option since pagination is not implemented, and we want to try to reduce the number of workflows
    //   returned for a single request as much as possible
    // - Parent workflows such as (AFTX_3PL_BULK_USER_ONBOARDING, BASELINING_ALL, etc.) are hidden since all they do is
    //   trigger child workflows which are what a user actually cares about
    // - The MFA_REGISTRATION workflow is hidden since the status and all actions (such as generate a new OTP) can be
    //   accomplished from the MFA Registration modal itself
    const workflowTypes = [
        {label: WorkflowType.AFTX_3PL_ASSOCIATE_ONBOARDING.toUpperCase(), value: WorkflowType.AFTX_3PL_ASSOCIATE_ONBOARDING},
        {label: WorkflowType.AFTX_3PL_USER_ADMIN_ONBOARDING.toUpperCase(), value: WorkflowType.AFTX_3PL_USER_ADMIN_ONBOARDING},
        {label: WorkflowType.USER_TERMINATION.toUpperCase(), value: WorkflowType.USER_TERMINATION},
        {label: WorkflowType.BASELINING_ASSOCIATE.toUpperCase(), value: WorkflowType.BASELINING_ASSOCIATE},
        {label: WorkflowType.BASELINING_USER_ADMIN_PRIMARY.toUpperCase(), value: WorkflowType.BASELINING_USER_ADMIN_PRIMARY},
        {label: WorkflowType.BASELINING_USER_ADMIN_SECONDARY.toUpperCase(), value: WorkflowType.BASELINING_USER_ADMIN_SECONDARY},
    ];

    // The list doesn't include WorkflowStatus.COMPLETED since that would probably cause confusion if displayed
    const workflowStatuses = [
        {label: t('all'), value: WorkflowStatus.ALL},
        {label: t('in-progress'), value: WorkflowStatus.ACTIVE},
        {label: t('succeeded'), value: WorkflowStatus.SUCCEEDED},
        {label: t('failed'), value: WorkflowStatus.FAILED}
    ];

    const relativeTimes = RELATIVE_TIME_RANGE_OPTIONS.map(option => ({
        label: t(option.labelKey),
        value: option.value,
        ms: option.ms
    }));

    const [workflowCards, setWorkflowCards] = useState<Map<string, WorkflowCard>>(new Map());
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [scopeOptions, setScopeOptions] = useState<any>([]);
    const [scope, setScope] = useState<string>();
    const [startTime, setStartTime] = useState<Date>(new Date(Date.now() - FIFTEEN_DAYS_IN_MILLISECONDS));
    const [endTime, setEndTime] = useState<Date>(new Date());
    const [timeMode, setTimeMode] = useState<TimeMode>('relative');
    const [relativeTime, setRelativeTime] = useState<RelativeTimeRange>('1week');

    const [workflowStatus, setWorkflowStatus] = useState<string>(workflowStatuses[1].value);
    const [workflowType, setWorkflowType] = useState<string>(workflowTypes[0].value);

    const [listWorkflows, {loading}] = useLazyQuery(LIST_WORKFLOWS_QUERY, {
        fetchPolicy: NETWORK_ONLY, // don't cache
        onCompleted: data => {
            logger.info(`Retrieved ${data.listWorkflows.workflowDetails.length} records from listWorkflows.`);
            graphqlClientMetricsPublisher.publishCounterMonitor('list-workflows.SUCCESS', 1);

            const workflows: WorkflowDetails[] = data.listWorkflows.workflowDetails;

            // Create new map for workflow cards (clear previous results)
            const newWorkflowCards = new Map<string, WorkflowCard>();
            
            // Add workflows to the new map
            for (const workflow of workflows) {
                newWorkflowCards.set(workflow.workflowId, {
                    items: getCardItems(workflow),
                    expanded: false,
                    header: getHeader(workflow),
                    state: workflow.workflowState as WorkflowState
                });
            }

            setWorkflowCards(newWorkflowCards);
            setErrorMessage(undefined);
        },
        onError: error => {
            logger.error("Call to listWorkflows failed!", error);
            graphqlClientMetricsPublisher.publishCounterMonitor('list-workflows.ERROR', 1);
            setWorkflowCards(new Map());
            setErrorMessage(error.message);
        }
    });

    // Executes one time when page first loads
    useEffect(() => {
        props.setActivePage(PageType.WORKFLOW_LIST);
        populateScopes();
    }, []);

    /**
     * Populates the filter with the scopes to which the current user has access.
     */
    const populateScopes = async() => {
        const scopes = await auth.getScopes();

        if (scopes && scopes.length > 0) {
            const scopeOptions = scopes.map((scope: string) => ({
                label: scope,
                value: scope
            }));
            
            // Add "All" option at the beginning
            const allOptions = [{ label: 'All', value: ALL_SCOPES }, ...scopeOptions];
            setScopeOptions(allOptions);
            setScope(ALL_SCOPES); // Default to "All"
        } else {
            // Fallback to ALL_SCOPES if couldn't get scopes
            logger.warn('Could not retrieve user scopes, falling back to ALL_SCOPES');
            setScopeOptions([{ label: 'All', value: ALL_SCOPES }]);
            setScope(ALL_SCOPES);
        }
    }

    // Update absolute dates when relative time changes
    useEffect(() => {
        if (timeMode === 'relative') {
            const now = Date.now();
            const selectedTime = relativeTimes.find(rt => rt.value === relativeTime);
            if (selectedTime?.ms) {
                setStartTime(new Date(now - selectedTime.ms));
                setEndTime(new Date(now));
            }
        }
    }, [timeMode, relativeTime]);

    // Executes every time one of the filters changes
    useEffect(() => {
        if (scope !== undefined) { // Only call when scope is initialized
            callListWorkflows();
        }
    }, [workflowType, workflowStatus, scope, startTime, endTime, timeMode, relativeTime]);

    /**
     * Calls to get the list of workflows.
     */
    const callListWorkflows = (token?: string) => {
        const variables: any = {
            listWorkflowsInput: {
                tenantId: TenantID.AFTX,
                includeChildWorkflows: true,
                workflowType: workflowType,
            }
        };

        // Only add time filters if not "all" in relative mode
        if (timeMode === 'absolute' || (timeMode === 'relative' && relativeTime !== 'all')) {
            variables.listWorkflowsInput.startTime = startTime.getTime();
            variables.listWorkflowsInput.endTime = endTime.getTime();
        }

        if (workflowStatus !== WorkflowStatus.ALL) {
            variables.listWorkflowsInput.workflowStatus = workflowStatus;
        }

        // Only add scope if not "All"
        if (scope && scope !== ALL_SCOPES) {
            variables.listWorkflowsInput.scope = scope;
        }

        logger.info("Calling listWorkflows.", variables.listWorkflowsInput);
        graphqlClientMetricsPublisher.publishCounterMonitor('list-workflows.INVOCATION', 1);
        listWorkflows({
            variables: variables,
        });
    }

    /**
     * Gets the header for the workflow card.
     *
     * @param workflow The workflow details from which to get the data for the header.
     */
    const getHeader = (workflow: WorkflowDetails) => {
        const workflowStateData = JSON.parse(workflow.workflowStateData!);
        const scope = workflowStateData["scope"];

        let header = `${workflow.workflowType.toLocaleUpperCase(locale)}`;

        if (
            (WorkflowType.AFTX_3PL_ASSOCIATE_ONBOARDING === workflow.workflowType) ||
            (WorkflowType.AFTX_3PL_USER_ADMIN_ONBOARDING === workflow.workflowType)
        ) {
            const firstName = workflowStateData["legalName.firstName"];
            const lastName = workflowStateData["legalName.lastName"];
            header += `: ${scope} - ${firstName} ${lastName}`;
        } else if (WorkflowType.USER_TERMINATION === workflow.workflowType) {
            const firstName = workflowStateData["firstName"];
            const lastName = workflowStateData["lastName"];
            header += `: ${scope} - ${firstName} ${lastName}`;
        } else if (
            (WorkflowType.BASELINING_ASSOCIATE === workflow.workflowType) ||
            (WorkflowType.BASELINING_USER_ADMIN_PRIMARY === workflow.workflowType) ||
            (WorkflowType.BASELINING_USER_ADMIN_SECONDARY === workflow.workflowType)
        ) {
            header += `: ${scope} - ${convertDateToLocaleFormat(locale, new Date(workflow.creationDate))}`;
        }

        return header;
    }

    /**
     * Gets the list of {@link CardItem} from the {@link WorkflowDetails} object.
     *
     * @param workflow The workflow from which to get the data for the card items.
     */
    const getCardItems = (workflow: WorkflowDetails): CardItem[] => {
        return [
            {
                attributeName: t('current-step'),
                attributeValue: workflow.workflowCurrentStep.toLocaleUpperCase(locale),
            },
            {
                attributeName: t('creation-date'),
                attributeValue: workflow.creationDate ?
                    convertDateTimeToLocaleFormat(locale, new Date(workflow.creationDate)) : '',
            },
            {
                attributeName: t('completion-date'),
                attributeValue: workflow.completionDate ?
                    convertDateTimeToLocaleFormat(locale, new Date(workflow.completionDate)) : '',
            },
            {
                attributeName: t('workflow-id'),
                attributeValue: workflow.workflowId
            }
        ];
    }

    /**
     * Expands all workflow cards.
     *
     * @param workflowCards The workflow cards to expand.
     */
    const expandAllCards = (workflowCards: Map<string, WorkflowCard>) => {
        for (const workflowId of workflowCards.keys()) {
            const workflowCard = workflowCards.get(workflowId)!;
            workflowCards.set(workflowId, {
                items: workflowCard.items,
                expanded: true,
                header: workflowCard.header,
                state: workflowCard.state
            });
        }

        setWorkflowCards(new Map(workflowCards));
    }

    /**
     * Collapses all workflow cards.
     *
     * @param workflowCards The workflow cards to collapse.
     */
    const collapseAllCards = (workflowCards: Map<string, WorkflowCard>) => {
        for (const workflowId of workflowCards.keys()) {
            const workflowCard = workflowCards.get(workflowId)!;
            workflowCards.set(workflowId, {
                items: workflowCard.items,
                expanded: false,
                header: workflowCard.header,
                state: workflowCard.state
            });
        }

        setWorkflowCards(new Map(workflowCards));
    }

    /**
     * Expands the workflow card if it's collapsed or collapses the workflow card if it's expanded.
     *
     * @param workflowId The workflow id of the workflow card to expand or collapse.
     */
    const toggleExpand = (workflowId: string) => {
        const workflowCard = workflowCards.get(workflowId)!;
        workflowCards.set(workflowId, {
            items: workflowCard.items,
            expanded: !workflowCard.expanded,
            header: workflowCard.header,
            state: workflowCard.state
        });

        setWorkflowCards(new Map(workflowCards));
    }

    /**
     * Refreshes the list of workflows.
     */
    const refresh = () => {
        logger.info("Refreshing list of workflows.");
        callListWorkflows();
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <BreadCrumbs breadcrumbItems={[{tag: t('workflows'), path: '/workflow'}]}/>
            </div>
            <div className="row b-background mx-0">
                <div className="col align-self-center title m-0 py-1">{t('workflows')}</div>
            </div>
            <div id="workflow-filter-menu" className="b-background mx-0 py-1">
                <div className="row align-items-center mx-1">
                    <div className="col-lg-6 p-1">
                        <Dropdown id="workflows-type-filter"
                                  label={t('workflow-type')}
                                  options={workflowTypes}
                                  value={workflowType}
                                  onChange={e => setWorkflowType(e.target.value)}/>
                    </div>
                    <div className="col-lg-6 p-1">
                        <Dropdown id="workflows-status-filter"
                                  label={t('workflow-status')}
                                  options={workflowStatuses}
                                  value={workflowStatus}
                                  onChange={e => setWorkflowStatus(e.target.value)}/>
                    </div>
                </div>
                <div className="row align-items-end mx-1">
                    <div className="col-lg-4 p-1">
                        <ButtonGroup
                            id="workflows-time-mode-selector"
                            value={timeMode}
                            onChange={(e: any) => setTimeMode(e.target.value)}
                            buttons={TIME_MODE_OPTIONS.map(option => ({
                                id: `workflows-${option.value}-time`,
                                label: t(option.labelKey),
                                value: option.value
                            }))}
                        />
                    </div>
                </div>
                <div className="row align-items-center mx-1">
                    {timeMode === 'relative' ? (
                        <div className="col-lg-4 p-1">
                            <Dropdown
                                id="workflows-relative-time-filter"
                                label={t('time-range')}
                                options={relativeTimes}
                                value={relativeTime}
                                onChange={e => setRelativeTime(e.target.value)}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="col-lg-3 p-1">
                                <LocalizedDatepicker
                                    id="workflows-start-time-filter"
                                    label={t('creation-date-start')}
                                    selectedDate={startTime}
                                    onInput={(e: any) => setStartTime(new Date(e.target.selectedDate))}
                                />
                            </div>
                            <div className="col-lg-3 p-1">
                                <LocalizedDatepicker
                                    id="workflows-end-time-filter"
                                    label={t('creation-date-end')}
                                    selectedDate={endTime}
                                    onInput={(e: any) => setEndTime(new Date(e.target.selectedDate))}
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className="row align-items-center mx-1">
                    <div className="col-lg-6 p-1">
                        {scopeOptions.length > 0 && (
                            <Dropdown
                                id="workflows-scope-filter"
                                label={t('warehouse')}
                                options={scopeOptions}
                                value={scope}
                                onChange={e => setScope(e.target.value)}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div id="workflow-button-menu" className="row b-background mx-0 px-1 pt-1 pb-2">
                <div className="col px-1">
                    <div className="d-flex flex-row justify-content-end">
                        <Button
                            className="dashboard-buttons d-xl-block mr-1 "
                            icon="showDetail"
                            iconPosition="right"
                            label={t('expand-all')}
                            onClick={() => expandAllCards(workflowCards)}
                            id="workflows-expand-all"
                        />
                        <Button
                            className="dashboard-buttons d-xl-block mr-1 "
                            icon="hideDetail"
                            iconPosition="right"
                            label={t('collapse-all')}
                            onClick={() => collapseAllCards(workflowCards)}
                            id="workflows-collapse-all"
                        />
                        <Button
                            className="dashboard-buttons d-xl-block"
                            icon="refresh"
                            iconPosition="right"
                            onClick={() => refresh()}
                            id="workflows-refresh"
                        />
                    </div>
                </div>
            </div>
            <RenderWorkflowCards
                loading={loading}
                errorMessage={errorMessage}
                workflowCards={workflowCards}
                toggleExpand={toggleExpand}
            />
        </div>
    );

}